import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import CreateAction from '~/components/Actions/CreateAction';
import { Image } from '~/components/Image';
import Section from '~/components/Layout/Section';
import MarkDownRender from '~/components/Markdown/MarkDownRender';
import Typography from '~/components/Typography';
import AuthorsWidget from '~/components/Widgets/AuthorsPopover/AuthorsWidget';
import SocialIcons from '~/components/Widgets/SocialIcons';

import type { Publication } from '~/interfaces/publication';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useAuthors from '~/hooks/useLoaders/useAuthors';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import { BG_OPACITY_OPTIONS, BORDER_RADIUS_OPTIONS, PADDING_OPTIONS, SIDE_OPTIONS } from '~/utils/designOptions';
import normalizeFontType from '~/utils/normalizeFontType';

import type { HomePageLoaderData } from '~/routes';

const generateBorderSidesClass = (width: any, sides: any) => {
  const options = SIDE_OPTIONS[sides];

  return options[width];
};

const checkForSocialIcons = (publication: Publication) => {
  const tiktokUrl = publication.tiktok_url;
  const twitterUrl = publication.twitter_url;
  const youtubeUrl = publication.youtube_url;
  const facebookUrl = publication.facebook_url;
  const instagramUrl = publication.instagram_url;
  const linkedInUrl = publication.linkedin_url;
  const discordUrl = publication.discord_url;
  const threadsUrl = publication.threads_url;
  const isAtLeastOnePresent =
    tiktokUrl || twitterUrl || youtubeUrl || facebookUrl || instagramUrl || linkedInUrl || discordUrl || threadsUrl;

  return isAtLeastOnePresent;
};

const Default = () => {
  const { t } = useTranslation();
  const { isLoading, authors } = useAuthors();
  const { homePage, publication } = useTypedLoaderData<HomePageLoaderData>();
  const { general, content_container, headline, description, logo, signup_widget } = homePage.main_section;

  const opacityClass = BG_OPACITY_OPTIONS[general.bg_gloss_opacity || '0'];

  const borderRadiusClass = BORDER_RADIUS_OPTIONS[content_container.border_radius || 'rounded-none'];

  const borderClass = generateBorderSidesClass(content_container.border_width, content_container.border_sides);

  const paddingClass = PADDING_OPTIONS[content_container.padding || 'p-0'];
  const hasSocialIcons = checkForSocialIcons(publication);
  const { web_template } = publication;
  const isLoggedIn = useIsLoggedIn();

  return (
    <Section
      bgColor={general.bg_color}
      bgImage={homePage?.main_bg_image_url}
      className="relative bg-cover bg-center bg-no-repeat px-4 pb-4 pt-16"
    >
      <div
        className={cx('absolute left-0 top-0 h-full w-full', opacityClass)}
        style={{
          backgroundColor: general.bg_color,
        }}
      />
      <div className="relative mx-auto max-w-6xl">
        <div
          className={cx(
            'mx-auto mb-10 w-full',
            content_container.padding === 'none' ? 'max-w-xl' : 'max-w-2xl',
            paddingClass,
            borderRadiusClass,
            borderClass,
          )}
          style={{
            borderColor: content_container.border_color,
            backgroundColor: content_container.bg_color,
          }}
        >
          <div className="flex-start flex flex-col items-center py-2">
            {logo.enabled && (
              <div
                className={cx({
                  'mb-4 overflow-hidden rounded': true,
                  'h-16 w-16': logo.size === 'sm',
                  'h-20 w-20': logo.size === 'md',
                  'h-24 w-24': logo.size === 'lg',
                  'shadow-sm': logo.shadow === 'sm',
                  'shadow-md': logo.shadow === 'md',
                  'shadow-lg': logo.shadow === 'lg',
                  'shadow-xl': logo.shadow === 'xl',
                })}
              >
                <Image maxWidth={128} aspectRatio={'square'} src={publication.logo.url} alt={publication.name} />
              </div>
            )}
            <div className="space-y-1">
              <Typography
                as="h1"
                size={headline.size}
                weight="bold"
                fontFamily={headline.font}
                color={headline.color}
                className="w-full text-center"
              >
                <MarkDownRender>{headline.text}</MarkDownRender>
              </Typography>
              <Typography
                as="div"
                size={description.size}
                weight="regular"
                fontFamily={description.font}
                color={description.color}
                className="w-full text-center"
              >
                <MarkDownRender>{description.text}</MarkDownRender>
              </Typography>
            </div>
          </div>

          <div className="mx-auto mt-4 max-w-xl">
            {isLoggedIn && web_template.logged_subscribe_form ? (
              <></>
            ) : (
              <CreateAction
                publication={publication}
                fontFamily={normalizeFontType(signup_widget.font)}
                placeholderText={signup_widget.placeholder_text}
                cta={signup_widget.cta_text}
                textColor={signup_widget.text_color}
                backgroundColor={signup_widget.bg_color}
              />
            )}
          </div>
        </div>

        <div className="flex items-end justify-between">
          {!isLoading && publication.render_authors_widget && (
            <AuthorsWidget authors={authors} publication={publication} homePage={homePage} />
          )}

          {homePage.main_section.social_widget.enabled && hasSocialIcons && (
            <div>
              <Typography
                as="p"
                fontFamily={homePage.general_section.theme.font}
                color={homePage.main_section.social_widget.text_color}
                className="mb-1 whitespace-nowrap text-xs font-semibold"
              >
                {homePage.main_section.social_widget.label || t('shared.cta.connect')}
              </Typography>
              <Typography
                as="span"
                fontFamily={homePage.general_section.theme.font}
                color={homePage.main_section.social_widget.text_color}
                className="mb-0.5 whitespace-nowrap text-xs font-semibold"
              >
                <SocialIcons publication={publication} size="sm" />
              </Typography>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default Default;
